import type { FollowUpLocation, FollowUpsSnapshot, Invite, Message, MessageAuthor, ReportAccess } from '@bunker/shared';

/**
 * `?mock=full`: the account and server-admin reads the design pass opens —
 * reminders, devices, blocks, moderation access, invites. Without them these
 * requests fell through to the generic `{ ok: true }`, and «Напоминания»
 * crashed on it (found by the UI gallery, scripts/ui-gallery.mjs).
 */
// Relative to now, so a reminder «in three hours» is not already overdue.
const at = (minutes: number) => new Date(Date.now() + minutes * 60_000).toISOString();

const place = (channelId: string, title: string): FollowUpLocation => ({
  channelId, kind: 'channel', title, guildId: 'g_cispracs', guildName: 'cispracs · core', avatarUrl: null, initials: 'CC', color: null,
});

const author = (id: string, username: string, displayName: string): MessageAuthor => ({
  id, platformId: `p_${username}`, username, displayName, avatarUrl: null, teamTag: null,
});

const message = (id: string, channelId: string, who: MessageAuthor, content: string, minutes: number): Message => ({
  id, channelId, authorId: who.id, content, attachments: null, mentions: [], replyToId: null, replyTo: null,
  pinned: false, editedAt: null, deletedAt: null, createdAt: at(minutes), author: who, reactions: [],
});

const kostyl = author('u_kostyl', 'kostyl', 'Костыль');
const b1ad = author('u_b1ad', 'b1ad', 'b1ad');

/** One reminder in each state the inbox shows: due later, overdue, no time, done — and one whose message is gone. */
const FOLLOW_UPS: FollowUpsSnapshot = {
  items: [
    {
      id: 'fu_mock_later', messageId: 'm_mock_cfg', channelId: 'c_general', remindAt: at(180), note: null, status: 'pending',
      createdAt: at(-30), completedAt: null, location: place('c_general', '#general'),
      message: message('m_mock_cfg', 'c_general', kostyl, 'Скиньте свои cfg до вечера, соберём общий для команды', -35),
    },
    {
      id: 'fu_mock_overdue', messageId: 'm_mock_roster', channelId: 'c_general', remindAt: at(-45), note: null, status: 'pending',
      createdAt: at(-600), completedAt: null, location: place('c_general', '#general'),
      message: message('m_mock_roster', 'c_general', kostyl, 'Подтвердите состав на субботу до 20:00', -610),
    },
    {
      id: 'fu_mock_inbox', messageId: 'm_mock_demo', channelId: 'c_praks', remindAt: null, note: null, status: 'pending',
      createdAt: at(-240), completedAt: null, location: place('c_praks', '#praks'),
      message: message('m_mock_demo', 'c_praks', b1ad, 'Демка вчерашнего матча, раунды 14–16 — посмотрите выход на B', -250),
    },
    {
      id: 'fu_mock_gone', messageId: 'm_mock_deleted', channelId: 'c_praks', remindAt: at(-1500), note: null, status: 'done',
      createdAt: at(-2880), completedAt: at(-1490), location: place('c_praks', '#praks'), message: null,
    },
  ],
};

const SESSIONS = {
  sessions: [
    { id: 'sess_mock_desktop', current: true, createdAt: at(-4320), lastSeenAt: at(0), uaHint: 'Cispracs Desktop · Windows' },
    { id: 'sess_mock_phone', current: false, createdAt: at(-20160), lastSeenAt: at(-600), uaHint: 'Chrome · Android' },
  ],
};

const REPORT_ACCESS: ReportAccess = { platformAdmin: false, rootAdmin: false };

function invites(guildId: string): { invites: Invite[] } {
  return {
    invites: [
      { code: 'praks21', guildId, channelId: 'c_general', creatorId: 'me_0', expiresAt: at(10080), maxUses: null, uses: 14, createdAt: at(-2880) },
      { code: 'major26', guildId, channelId: null, creatorId: 'u_kostyl', expiresAt: null, maxUses: 50, uses: 3, createdAt: at(-600) },
    ],
  };
}

/** The mock answer for one of these reads, or undefined when `path` is not one. */
export function handleMockAccount(path: string, method: string): unknown {
  if (method !== 'GET') return undefined;
  if (path === '/api/me/follow-ups') return FOLLOW_UPS;
  if (path === '/api/me/sessions') return SESSIONS;
  if (path === '/api/me/blocked-by') return { userIds: [] };
  if (path === '/api/reports/access') return REPORT_ACCESS;
  const invite = /^\/api\/guilds\/([^/]+)\/invites$/.exec(path);
  if (invite) return invites(invite[1]!);
  return undefined;
}
